import Honeybadger from '@honeybadger-io/js';
import { BROWSER_TYPES, OS_TYPES, SPECIAL_DATE_RANGES } from '../_global/constants';
import { KNOWN_BROWSERS, KNOWN_OPERATING_SYSTEMS } from './constants';
import { capitalizeFirstLetter } from '/src/utils/string';

import { formatMinimalDate, formatShortMinimalDate, normalizeDate } from '/src/utils/date';

import {
  startOfDay,
  endOfDay,
  isSameYear,
  addDays,
  fromUnixTime,
  startOfMonth,
  endOfMonth,
  addMonths,
  isSameDay,
  min,
  subDays,
  isAfter,
  differenceInDays,
  isBefore,
  getUnixTime,
} from 'date-fns';

export const getOptionDisplayValue = (option) => {
  return option.label ?? option.value;
};

export const initialSelection = () => ({
  utmSource: [],
  utmMedium: [],
  utmCampaign: [],
  utmTerm: [],
  utmContent: [],
  referrerUrl: [],
});

export const getNameForParam = (utmParam) => {
  switch (utmParam) {
    case 'referrerUrl':
      return 'Referrer';
    case 'utmCampaign':
      return 'Campaign name';
    default:
      return `Campaign ${utmParam.replace('utm', '')}`;
  }
};

export const getEmptyText = (param) => {
  return param === 'referrerUrl' ? 'No referrer set' : `No ${param.replace('utm', '').toLowerCase()} set`;
};

export const printParamCombination = (selection) => {
  let text = '';
  Object.keys(selection)
    .filter((x) => selection[x].length > 0)
    .forEach((x, i) => {
      if (i > 0) text += ' + ';
      text += getNameForParam(x) + ': ';
      selection[x].forEach((y, index) => (text += `${index > 0 ? ', ' : ''}${getOptionDisplayValue(y)}`));
    });
  return text;
};

export function percentizeValue(value, defaultValue = null, multiply = true) {
  if (!Number.isFinite(value)) {
    return defaultValue;
  }

  const percentage = multiply ? value * 100 : value;

  // round to 1 decimal place to avoid problems with floating-point errors
  // like: 55.00000000000001%
  const rounded = Math.round(percentage * 10) / 10;

  if (rounded % 1 !== 0) {
    return `${rounded.toFixed(1)}%`;
  }
  return `${rounded.toString()}%`;
}

export function formatBrowserName(browser) {
  const key = constantizeString(browser);

  switch (key) {
    case BROWSER_TYPES.GSA:
      return 'Google Search Appliance';
    case BROWSER_TYPES.OTHER:
    case 'UNKNOWN':
      return 'Unknown';
    default:
      if (!KNOWN_BROWSERS.includes(key)) {
        Honeybadger.notify('Analytics: Unknown browser', {
          context: {
            browser,
          },
        });
      }
      return browser;
  }
}

// Older browsers might not support `Intl.DusplayNames`
// Fall back to a dummy shim.
const _displayNamesFallback = {
  of: (value) => value,
};

export const countryNames =
  typeof Intl?.DisplayNames === 'function' ? new Intl.DisplayNames(['en'], { type: 'region' }) : _displayNamesFallback;

export const getCountryName = (raw) => {
  const input = (raw ?? '').trim().toUpperCase();

  if (!input) return 'Not set';

  const result = countryNames.of(input);

  if (!result || result === 'und') {
    Honeybadger.notify('Analytics: Unknown country', {
      context: {
        input,
        country: result,
      },
    });
    return 'Unknown country';
  }

  return result;
};

const languageNames =
  typeof Intl?.DisplayNames === 'function'
    ? new Intl.DisplayNames(['en'], { type: 'language' })
    : _displayNamesFallback;

export const getLanguageName = (raw) => {
  const input = (raw ?? '').trim().toLowerCase();

  if (!input) return 'Not set';
  if (input === '*') return 'Not specified';

  const languageCode = input.split('-')[0];

  if (languageCode.length !== 2) {
    Honeybadger.notify('Analytics: Incorrect language code', {
      context: {
        input,
        language: languageCode,
      },
    });
    return raw; // it is likely something like English (United States)
  }

  const result = languageNames.of(languageCode);

  if (!result || result === 'und') {
    Honeybadger.notify('Analytics: Unknown language', {
      context: {
        input,
        language: result,
      },
    });
    return 'Unknown language';
  }

  return result;
};

export function formatOsName(os) {
  switch (constantizeString(os)) {
    case OS_TYPES.WINDOWS:
    case 'WIN32':
      return 'Windows';
    case OS_TYPES.MACOS:
    case 'DARWIN':
      return 'MacOS';
    case OS_TYPES.ANDROID:
      return 'Android';
    case OS_TYPES.IOS:
      return 'iOS';
    case OS_TYPES.LINUX:
      return 'Linux';
    case OS_TYPES.WINDOWS_PHONE:
      return 'Windows Phone';
    case OS_TYPES.UBUNTU:
      return 'Ubuntu';
    case OS_TYPES.CHROMEOS:
      return 'ChromeOS';
    case OS_TYPES.CHROMIUM_OS:
      return 'Chromium OS';
    case OS_TYPES.OTHER:
    case 'UNKNOWN':
      return 'Unknown';
    default: {
      if (!KNOWN_OPERATING_SYSTEMS.includes(constantizeString(os))) {
        Honeybadger.notify('Analytics: Unknown OS', {
          context: {
            os,
          },
        });
      }
      return os;
    }
  }
}

export function recentlyEnabled(enabledAt) {
  if (!Number.isFinite(enabledAt)) return false;

  const enabledDate = normalizeDate(enabledAt);
  const sixtyDaysAgo = subDays(new Date(), 60);

  return isAfter(enabledDate, sixtyDaysAgo);
}

export function constantizeString(input) {
  return input.toUpperCase().replaceAll(' ', '_');
}

export function humanizeMetric(metric) {
  if (!metric) return null;
  switch (metric) {
    case 'pageViews':
      return 'Pageviews';
    case 'avgSessionDuration':
      return 'Avg session duration';
    case 'avgTimeOnPage':
      return 'Avg time on page';
    case 'bounceRate':
      return 'Bounce rate';
    default:
      return capitalizeFirstLetter(metric);
  }
}

export function humanizeDimension(dimension, plural = false) {
  if (!dimension) return null;
  switch (dimension) {
    case 'countries':
      return plural ? 'Countries' : 'Country';
    case 'languages':
      return plural ? 'Languages' : 'Language';
    case 'os':
      return plural ? 'Operating systems' : 'Operating system';
    case 'browsers':
      return plural ? 'Browsers' : 'Browser';
    case 'channels':
      return plural ? 'Channels' : 'Channel';
    case 'referrers':
      return plural ? 'Referrers' : 'Referrer';
    case 'pages':
      return plural ? 'Pages' : 'Page';
    case 'entryPages':
      return plural ? 'Entry pages' : 'Entry page';
    case 'utmContents':
      return plural ? 'UTM Contents' : 'UTM Content';
    case 'utmTerms':
      return plural ? 'UTM Terms' : 'UTM Term';
    case 'utmMediums':
      return plural ? 'UTM Mediums' : 'UTM Medium';
    case 'utmSources':
      return plural ? 'UTM Sources' : 'UTM Source';
    case 'utmCampaigns':
      return plural ? 'UTM Campaigns' : 'UTM Campaign';
    case 'deviceType':
      return 'Device Type';
    default: {
      // fall back to capitalizing the first letter and adding a basic plural if needed
      const base = capitalizeFirstLetter(dimension);
      return plural ? (base.endsWith('s') ? base : `${base}s`) : base;
    }
  }
}

export function parseDateRange(input, min, max, maxDays = 365) {
  if (!input) return [null, null, null];

  switch (input) {
    case SPECIAL_DATE_RANGES.TODAY:
    case SPECIAL_DATE_RANGES.LAST_7_DAYS:
    case SPECIAL_DATE_RANGES.LAST_30_DAYS:
    case SPECIAL_DATE_RANGES.THIS_MONTH:
    case SPECIAL_DATE_RANGES.LAST_MONTH:
      return [null, null, input];
    default: {
      if (!input.includes(',')) return [null, null, null];

      const parts = input.split(',').map((part) => Number.parseInt(part.trim(), 10));
      if (parts.length !== 2 || !parts.every(Number.isFinite)) return [null, null, null];

      if (maxDays < 1) return [null, null, null];

      // normalize incoming ints to Date instances
      let [start, end] = parts.map(normalizeDate);

      // if they are swapped, flip them.
      if (start > end) {
        const t = start;
        start = end;
        end = t;
      }

      const minDate = min ? normalizeDate(min) : null;
      const maxDate = max ? normalizeDate(max) : null;

      // enforce maxDays as an inclusive window
      const span = Math.abs(differenceInDays(end, start)) + 1;
      if (span > maxDays) {
        start = subDays(end, maxDays - 1);
      }

      // clamp to [minDate, maxDate] if provided
      if (minDate && isBefore(start, minDate)) {
        start = minDate;
      }
      if (maxDate && isAfter(end, maxDate)) {
        end = maxDate;
      }

      if (start > end) return [null, null, null];

      return [getUnixTime(start), getUnixTime(end), null];
    }
  }
}

export function humanizeDateRange([startDate, endDate, predefinedRange]) {
  if (predefinedRange) {
    const [predefinedStart, predefinedEnd] = parsePredefinedRange(predefinedRange);

    if (!Number.isFinite(predefinedStart) || !Number.isFinite(predefinedEnd)) {
      return '';
    }

    const parsedStart = fromUnixTime(predefinedStart);
    const parsedEnd = fromUnixTime(predefinedEnd);

    if (isSameDay(parsedEnd, parsedStart)) {
      return `${formatMinimalDate(predefinedEnd)}`;
    }
    if (isSameYear(parsedEnd, parsedStart)) {
      return `${formatShortMinimalDate(predefinedStart)} - ${formatMinimalDate(predefinedEnd)}`;
    } else {
      return `${formatMinimalDate(predefinedStart)} - ${formatMinimalDate(predefinedEnd)}`;
    }
  } else {
    if (!Number.isFinite(startDate) || !Number.isFinite(endDate)) {
      return '';
    }

    const parsedStart = fromUnixTime(startDate);
    const parsedEnd = fromUnixTime(endDate);

    if (isSameDay(parsedEnd, parsedStart)) {
      return `${formatMinimalDate(endDate)}`;
    }
    if (isSameYear(parsedEnd, parsedStart)) {
      return `${formatShortMinimalDate(startDate)} - ${formatMinimalDate(endDate)}`;
    } else {
      return `${formatMinimalDate(startDate)} - ${formatMinimalDate(endDate)}`;
    }
  }
}

export function parsePredefinedRange(string) {
  const now = Date.now();
  let startDate, endDate;
  switch (string) {
    case SPECIAL_DATE_RANGES.TODAY:
      startDate = startOfDay(now) / 1000;
      endDate = endOfDay(now) / 1000;
      break;
    case SPECIAL_DATE_RANGES.LAST_7_DAYS:
      startDate = startOfDay(addDays(now, -6)) / 1000; // inclusive of today => 7 days
      endDate = endOfDay(now) / 1000;
      break;
    case SPECIAL_DATE_RANGES.LAST_30_DAYS:
      startDate = startOfDay(addDays(now, -29)) / 1000; // inclusive of today => 30 days
      endDate = endOfDay(now) / 1000;
      break;
    case SPECIAL_DATE_RANGES.THIS_MONTH:
      startDate = startOfMonth(now) / 1000;
      endDate = min([endOfMonth(now), endOfDay(now)]) / 1000;
      break;
    case SPECIAL_DATE_RANGES.LAST_MONTH:
      startDate = startOfMonth(addMonths(now, -1)) / 1000;
      endDate = endOfMonth(addMonths(now, -1)) / 1000;
      break;
    default:
      return [null, null];
  }

  return [startDate, endDate];
}
